import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Flame, Clock, ChevronRight, X } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';

const DEALS = [
  { country: 'Japan', flag: 'JP', discount: 15, originalPrice: 8.99, dealPrice: 7.64, plan: '5GB / 7 Days' },
  { country: 'United States', flag: 'US', discount: 20, originalPrice: 12.50, dealPrice: 10.00, plan: '10GB / 15 Days' },
  { country: 'Thailand', flag: 'TH', discount: 25, originalPrice: 6.99, dealPrice: 5.24, plan: '3GB / 7 Days' },
  { country: 'Turkey', flag: 'TR', discount: 18, originalPrice: 9.50, dealPrice: 7.79, plan: '5GB / 15 Days' },
  { country: 'United Kingdom', flag: 'GB', discount: 10, originalPrice: 7.99, dealPrice: 7.19, plan: '3GB / 7 Days' }
];

function getCountryFlag(code) {
  return code.toUpperCase().replace(/./g, c => String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65));
}

function useCountdown() {
  const [timeLeft, setTimeLeft] = useState({});

  useEffect(() => {
    const getEndOfDay = () => {
      const now = new Date();
      const end = new Date(now);
      end.setHours(23, 59, 59, 999);
      return end;
    };

    const update = () => {
      const diff = getEndOfDay() - new Date();
      if (diff <= 0) return { h: 0, m: 0, s: 0 };
      setTimeLeft({
        h: Math.floor(diff / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000)
      });
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);

  return timeLeft;
}

export default function DealBanner({ onViewDeal }) {
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();
  const [dismissed, setDismissed] = useState(false);
  const countdown = useCountdown();

  const todayDeal = useMemo(() => {
    const dayIndex = new Date().getDate() % DEALS.length;
    return DEALS[dayIndex];
  }, []);

  if (dismissed) return null;

  const pad = (n) => String(n).padStart(2, '0');

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="relative bg-gradient-to-r from-teal-500/10 via-indigo-500/10 to-teal-500/10 border-b border-teal-500/20"
        data-testid="deal-banner"
      >
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1 min-w-0">
            <div className="flex items-center space-x-2 flex-shrink-0">
              <Flame className="w-4 h-4 text-orange-400" />
              <span className="text-xs font-bold uppercase tracking-wider text-orange-400 hidden sm:inline">{t('deal.title')}</span>
            </div>

            <div className="flex items-center space-x-3 min-w-0">
              <span className="text-lg">{getCountryFlag(todayDeal.flag)}</span>
              <div className="min-w-0">
                <p className="text-white text-sm font-semibold truncate">
                  eSIM {todayDeal.country} — {todayDeal.plan}
                </p>
                <div className="flex items-center space-x-2">
                  <span className="text-slate-500 text-xs line-through">{formatPrice(todayDeal.originalPrice)}</span>
                  <span className="text-teal-400 text-sm font-bold font-mono">{formatPrice(todayDeal.dealPrice)}</span>
                  <span className="bg-rose-500/20 text-rose-400 text-xs font-bold px-1.5 py-0.5 rounded">-{todayDeal.discount}%</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4 flex-shrink-0">
            <div className="hidden md:flex items-center space-x-1.5 text-slate-400">
              <Clock className="w-3.5 h-3.5" />
              <div className="flex space-x-1 font-mono text-xs">
                <span className="bg-white/10 rounded px-1.5 py-0.5 text-white">{pad(countdown.h || 0)}</span>
                <span>:</span>
                <span className="bg-white/10 rounded px-1.5 py-0.5 text-white">{pad(countdown.m || 0)}</span>
                <span>:</span>
                <span className="bg-white/10 rounded px-1.5 py-0.5 text-white">{pad(countdown.s || 0)}</span>
              </div>
            </div>

            <button
              onClick={() => onViewDeal?.(todayDeal.country)}
              className="bg-teal-500 hover:bg-teal-400 text-black text-xs font-bold px-4 py-1.5 rounded-full transition-all flex items-center space-x-1"
              data-testid="deal-cta"
            >
              <span>{t('deal.viewDeal')}</span>
              <ChevronRight className="w-3 h-3" />
            </button>

            <button
              onClick={() => setDismissed(true)}
              className="text-slate-500 hover:text-white p-1 transition-colors"
              data-testid="deal-dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
